import { z } from "zod";

/* -------------------------------------------------------------------------- */
/* Shared schemas                                                              */
/* -------------------------------------------------------------------------- */

export const Semver = z.string().regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/, "semver");
export const DesignId = z.string().regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/);
export const Channel = z.enum(["latest", "beta"]);
export type Channel = z.infer<typeof Channel>;

export const Params = z.record(z.string(), z.unknown());
export type Params = z.infer<typeof Params>;

/** Plain user text: control characters removed (newlines kept when `multiline`), trimmed, then length-checked. */
function plainText(max: number, multiline = false) {
  const strip = multiline ? /[\u0000-\u0009\u000b-\u001f\u007f]/g : /[\u0000-\u001f\u007f]/g;
  return z
    .string()
    .transform((s) => s.replace(strip, multiline ? "" : " ").trim())
    .pipe(z.string().max(max));
}

/** Canonical form of a fit-tag ref: trimmed, lowercased, inner whitespace removed. */
export function normalizeFitRef(ref: string): string {
  return ref.trim().toLowerCase().replace(/\s+/g, "");
}
export const FIT_REF_PATTERN = /^[a-z0-9][a-z0-9.-]{0,31}$/;
export const MAX_FITS = 10;

/**
 * Something a configuration is known to fit (for brickbrackets: a LEGO set number).
 * Generic on purpose; the brand decides the label and a stricter client-side pattern.
 */
export const FitTag = z.object({
  ref: z.string().transform(normalizeFitRef).pipe(z.string().regex(FIT_REF_PATTERN, "fit ref")),
  name: plainText(80).optional(),
});
export type FitTag = z.infer<typeof FitTag>;

/** Up to MAX_FITS tags, deduplicated by ref (first wins). */
export const FitTags = z
  .array(FitTag)
  .max(MAX_FITS)
  .transform((tags) => tags.filter((t, i) => tags.findIndex((o) => o.ref === t.ref) === i));

export const DisplayName = z.string().regex(/^[A-Za-z0-9_.-]{3,30}$/, "3–30 letters, digits, _ . -");

export const Me = z.object({
  sub: z.string(),
  email: z.string().optional(),
  name: z.string().optional(),
  picture: z.string().optional(),
  /** Public handle shown on the tags you add to community entries; never the email. */
  displayName: z.string().optional(),
  brand: z.string(),
  plan: z.enum(["free", "pro", "staff"]).default("free"),
  createdAt: z.string(),
});
export type Me = z.infer<typeof Me>;

export const SetProfileBody = z.object({ displayName: DisplayName });
export type SetProfileBody = z.infer<typeof SetProfileBody>;

export const DesignVersion = z.object({
  id: DesignId,
  version: Semver,
  pack: z.string(),
  bundleUrl: z.string(),
  hash: z.string(),
  kernel: z.enum(["manifold", "replicad"]).default("manifold"),
  jsonSchema: z.record(z.string(), z.unknown()),
  publishedAt: z.string(),
  changelog: z.string().optional(),
  deprecated: z.boolean().default(false),
  thumbnailUrl: z.string().optional(),
});
export type DesignVersion = z.infer<typeof DesignVersion>;

export const CatalogEntry = z.object({
  id: DesignId,
  pack: z.string(),
  title: z.string(),
  description: z.string().optional(),
  tags: z.array(z.string()).default([]),
  thumbnailUrl: z.string().optional(),
  channel: Channel,
  /** Version the channel resolves to right now. */
  version: Semver,
  bundleUrl: z.string(),
  hash: z.string(),
  kernel: z.enum(["manifold", "replicad"]).default("manifold"),
  complexity: z.enum(["low", "medium", "high"]).optional(),
  /** Pack family: designs sharing one parameter set (e.g. wall vs SKÅDIS mount). */
  family: z.string().optional(),
  /** Short name within the family, for the variant switcher. */
  variant: z.string().optional(),
});
export type CatalogEntry = z.infer<typeof CatalogEntry>;

export const SavedConfig = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  designId: DesignId,
  version: Semver,
  params: Params,
  brand: z.string(),
  revision: z.number().int().min(1),
  thumbnailKey: z.string().optional(),
  shareToken: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SavedConfig = z.infer<typeof SavedConfig>;

export const CreateConfigBody = z.object({
  name: z.string().min(1).max(120),
  designId: DesignId,
  version: Semver,
  params: Params,
});
export type CreateConfigBody = z.input<typeof CreateConfigBody>;

/** Every update writes a new revision row; the config row points at the newest. */
export const UpdateConfigBody = z.object({
  name: z.string().min(1).max(120).optional(),
  version: Semver.optional(),
  params: Params.optional(),
});
export type UpdateConfigBody = z.input<typeof UpdateConfigBody>;

export const ConfigRevision = z.object({
  configId: z.string(),
  revision: z.number().int(),
  version: Semver,
  params: Params,
  name: z.string(),
  createdAt: z.string(),
});
export type ConfigRevision = z.infer<typeof ConfigRevision>;

/* -------------------------------------------------------------------------- */
/* Community: parameter sets nobody owns, tagged by the people who printed them */
/* -------------------------------------------------------------------------- */

/**
 * Something one community entry fits (for brickbrackets: a LEGO set), owned by
 * the user who tagged it (`owner` = their display name). `confirmCount` counts
 * users who printed the entry for this ref, the owner included when they did.
 */
export const PublicFit = FitTag.extend({
  owner: z.string(),
  confirmCount: z.number().int().default(0),
  taggedAt: z.string(),
});
export type PublicFit = z.infer<typeof PublicFit>;

/** Whether anyone vouched that `fit` fits: at least one registered print. */
export function isFitConfirmed(fit: Pick<PublicFit, "confirmCount">): boolean {
  return fit.confirmCount > 0;
}

/**
 * One parameter combination of a design, listed because someone tagged what
 * it fits. Nobody owns it: it exists while it has visible tags, and each tag
 * belongs to its owner. Identified by design + full params, across versions;
 * `version` is the newest one it was tagged with and `params` are complete
 * (defaults of that version filled in), so it opens the same in any version.
 */
export const PublicConfigSummary = z.object({
  id: z.string(),
  designId: DesignId,
  version: Semver,
  /** Visible tags, oldest first. */
  fits: z.array(PublicFit).default([]),
  /** Display names of the users who tagged it, in the order they first did. */
  taggers: z.array(z.string()).default([]),
  /** Registered prints across all fits (one per user per ref). */
  confirmCount: z.number().int().default(0),
  /** Distinct users who registered a print for any fit. */
  printerCount: z.number().int().default(0),
  /** Users who saved it to their own list. */
  likeCount: z.number().int().default(0),
  /** Distinct signed-in users who downloaded this configuration unchanged. */
  downloadCount: z.number().int().default(0),
  /** First tag. */
  createdAt: z.string(),
  /** Last tag added, removed or printed. */
  updatedAt: z.string(),
});
export type PublicConfigSummary = z.infer<typeof PublicConfigSummary>;

/** A community entry as listed next to the configurator: everything needed to apply it in place. */
export const CommunityEntry = PublicConfigSummary.extend({
  params: Params,
});
export type CommunityEntry = z.infer<typeof CommunityEntry>;

export const PublicConfig = PublicConfigSummary.extend({
  params: Params,
  bundleUrl: z.string(),
});
export type PublicConfig = z.infer<typeof PublicConfig>;

/**
 * Tag one thing these params of a design fit. Lands on the entry for this
 * design and these full params (created on the first tag). A ref it already
 * lists stays with its owner; `printed` registers the user's print on it either way.
 */
export const TagBody = z.object({
  version: Semver,
  params: Params,
  fit: FitTag,
  printed: z.boolean().optional(),
});
export type TagBody = z.input<typeof TagBody>;

/** What the signed-in user did in the community: their tags and their registered prints (`${entryId}#${ref}`), and the entries they tagged. */
export const MyCommunity = z.object({
  tags: z.array(z.string()),
  prints: z.array(z.string()),
  items: z.array(CommunityEntry),
});
export type MyCommunity = z.infer<typeof MyCommunity>;

export const FitLookupResult = z.object({
  ref: z.string(),
  name: z.string().optional(),
  year: z.number().int().optional(),
  parts: z.number().int().optional(),
  imageUrl: z.string().optional(),
});
export type FitLookupResult = z.infer<typeof FitLookupResult>;

export const FitDirectoryEntry = z.object({ ref: z.string(), name: z.string().optional(), count: z.number().int() });
export type FitDirectoryEntry = z.infer<typeof FitDirectoryEntry>;

export const ConfirmBody = z.object({ ref: z.string().transform(normalizeFitRef) });
export type ConfirmBody = z.input<typeof ConfirmBody>;
/** Most tags one entry carries. */
export const MAX_PUBLIC_FITS = 30;

export const ReportReason = z.enum(["wrong-fit", "spam", "offensive", "other"]);
export type ReportReason = z.infer<typeof ReportReason>;
/** A report is about one tag: a wrong fit, spam or an offensive name. */
export const ReportBody = z.object({ ref: z.string().transform(normalizeFitRef), reason: ReportReason, note: plainText(300).optional() });
export type ReportBody = z.input<typeof ReportBody>;

/** A reported tag: the entry it is on, the tag and its owner. */
export const ModQueueItem = z.object({
  id: z.string(),
  designId: DesignId,
  ref: z.string(),
  name: z.string().optional(),
  owner: z.string(),
  status: z.enum(["live", "hidden"]),
  reportCount: z.number().int(),
  reports: z.array(z.object({ reason: ReportReason, note: z.string().optional(), at: z.string() })),
  firstReportAt: z.string(),
});
export type ModQueueItem = z.infer<typeof ModQueueItem>;

export const ModActionBody = z.object({ action: z.enum(["hide", "restore", "remove", "dismiss"]) });
export type ModActionBody = z.infer<typeof ModActionBody>;

export const SharedConfig = z.object({
  token: z.string(),
  name: z.string(),
  designId: DesignId,
  version: Semver,
  params: Params,
  brand: z.string(),
  bundleUrl: z.string(),
  sharedAt: z.string(),
});
export type SharedConfig = z.infer<typeof SharedConfig>;

export const AnalyticsEvent = z.object({
  type: z.enum(["view", "configure", "export", "save", "share"]),
  designId: DesignId,
  version: Semver.optional(),
  props: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
  ts: z.string().optional(),
});
export type AnalyticsEvent = z.infer<typeof AnalyticsEvent>;

export const RecordDownloadBody = z.object({
  /** Community entry the file was made from, when its params were downloaded unchanged. */
  community: z.string().max(64).optional(),
});
export type RecordDownloadBody = z.infer<typeof RecordDownloadBody>;

export const ApiError = z.object({ error: z.string(), code: z.string().optional(), details: z.unknown().optional() });
export type ApiError = z.infer<typeof ApiError>;

/* -------------------------------------------------------------------------- */
/* Route table: one source of truth for the client and the Lambda router.     */
/* -------------------------------------------------------------------------- */

const PublicPage = z.object({
  items: z.array(PublicConfigSummary),
  cursor: z.string().optional(),
  /** Fit refs a `fit` search resolved to (e.g. "42115" -> ["42115-1"]); absent without `fit`. */
  matched: z.array(z.string()).optional(),
});

/** Order matters: literal paths (`/public/fits`) must precede `/public/:id` for the router and API Gateway. */
export const routes = {
  me: { method: "GET", path: "/me", auth: true, res: Me },
  setProfile: { method: "PUT", path: "/me/profile", auth: true, body: SetProfileBody, res: Me },
  catalog: { method: "GET", path: "/catalog", auth: false, res: z.object({ designs: z.array(CatalogEntry) }) },
  designVersions: {
    method: "GET",
    path: "/designs/:id/versions",
    auth: false,
    res: z.object({ versions: z.array(DesignVersion), channels: z.record(z.string(), Semver) }),
  },
  /** Live community entries for a design and its family siblings (`designs`), newest first. */
  designCommunity: {
    method: "GET",
    path: "/designs/:id/community",
    auth: false,
    res: z.object({ items: z.array(CommunityEntry), designs: z.array(DesignId) }),
  },
  /** Count one download per signed-in user, per design and per community entry (not by its taggers). `added: false` when already counted. */
  recordDownload: {
    method: "POST",
    path: "/designs/:id/downloads",
    auth: true,
    body: RecordDownloadBody,
    res: z.object({ added: z.boolean(), downloadCount: z.number().int(), communityCount: z.number().int().optional() }),
  },
  /** Unique-user download count per design id. */
  downloadStats: { method: "GET", path: "/downloads", auth: false, res: z.object({ designs: z.record(z.string(), z.number().int()) }) },
  listConfigs: { method: "GET", path: "/configs", auth: true, res: z.object({ configs: z.array(SavedConfig) }) },
  createConfig: { method: "POST", path: "/configs", auth: true, body: CreateConfigBody, res: SavedConfig },
  getConfig: { method: "GET", path: "/configs/:id", auth: true, res: z.object({ config: SavedConfig, revisions: z.array(ConfigRevision) }) },
  updateConfig: { method: "PUT", path: "/configs/:id", auth: true, body: UpdateConfigBody, res: SavedConfig },
  deleteConfig: { method: "DELETE", path: "/configs/:id", auth: true, res: z.object({ ok: z.literal(true) }) },
  shareConfig: { method: "POST", path: "/configs/:id/share", auth: true, res: z.object({ token: z.string(), url: z.string() }) },
  getShared: { method: "GET", path: "/share/:token", auth: false, res: SharedConfig },
  /** Tag what a design's params fit (see `TagBody`); `added: false` when the entry already listed that ref (it stays its owner's). */
  tagDesign: { method: "POST", path: "/designs/:id/tags", auth: true, body: TagBody, res: z.object({ entry: CommunityEntry, added: z.boolean() }) },
  listFits: { method: "GET", path: "/public/fits", auth: false, res: z.object({ fits: z.array(FitDirectoryEntry) }) },
  /** Search the brand's external fit catalog (`?q=`, 2+ characters). 404 `lookup_disabled` when the brand has none. */
  lookupFits: { method: "GET", path: "/fits/lookup", auth: true, res: z.object({ results: z.array(FitLookupResult) }) },
  listPublic: { method: "GET", path: "/public", auth: false, res: PublicPage },
  getPublic: { method: "GET", path: "/public/:id", auth: false, res: PublicConfig },
  /** Register "I printed it for this fit" (`ref`): one per user per ref, the tag's owner included. `added: false` when already registered. */
  confirmPublic: { method: "POST", path: "/public/:id/confirm", auth: true, body: ConfirmBody, res: z.object({ added: z.boolean(), entry: CommunityEntry }) },
  /** Remove a tag: its owner or staff. `entry` is absent when that was its last tag (the entry is gone). */
  removeTag: { method: "DELETE", path: "/public/:id/fits/:ref", auth: true, res: z.object({ entry: CommunityEntry.optional() }) },
  /** Save (heart) an entry to your own list, or take it off. */
  likePublic: { method: "PUT", path: "/public/:id/like", auth: true, res: z.object({ liked: z.boolean(), likeCount: z.number().int() }) },
  unlikePublic: { method: "DELETE", path: "/public/:id/like", auth: true, res: z.object({ liked: z.boolean(), likeCount: z.number().int() }) },
  /** Live entries you saved, most recently saved first. */
  myLikes: { method: "GET", path: "/me/likes", auth: true, res: z.object({ items: z.array(CommunityEntry) }) },
  /** Your tags and prints, and the live entries you tagged, most recent first. */
  myCommunity: { method: "GET", path: "/me/community", auth: true, res: MyCommunity },
  reportPublic: { method: "POST", path: "/public/:id/report", auth: true, body: ReportBody, res: z.object({ ok: z.literal(true) }) },
  modQueue: { method: "GET", path: "/mod/queue", auth: true, res: z.object({ items: z.array(ModQueueItem) }) },
  /** Act on one reported tag. */
  modAction: { method: "POST", path: "/mod/public/:id/fits/:ref", auth: true, body: ModActionBody, res: z.object({ ok: z.literal(true) }) },
  track: { method: "POST", path: "/events", auth: false, body: z.object({ events: z.array(AnalyticsEvent).max(50) }), res: z.object({ ok: z.literal(true) }) },
} as const;

export type RouteName = keyof typeof routes;
export const BRAND_HEADER = "x-cfg-brand";

/* -------------------------------------------------------------------------- */
/* Typed fetch client                                                          */
/* -------------------------------------------------------------------------- */

export interface ApiClientOptions {
  baseUrl: string;
  brand: string;
  getToken?: () => Promise<string | null> | string | null;
  fetch?: typeof fetch;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError | undefined,
    message: string,
  ) {
    super(message);
  }
}

function fill(path: string, params: Record<string, string>): string {
  return path.replace(/:([a-zA-Z]+)/g, (_, k: string) => encodeURIComponent(params[k]));
}

export function createApiClient(opts: ApiClientOptions) {
  const f = opts.fetch ?? fetch;
  async function call<TRes>(
    route: { method: string; path: string; auth: boolean; res: z.ZodType<TRes> },
    args: { params?: Record<string, string>; body?: unknown; query?: Record<string, string | undefined> } = {},
  ): Promise<TRes> {
    const headers: Record<string, string> = { accept: "application/json", [BRAND_HEADER]: opts.brand };
    if (args.body !== undefined) headers["content-type"] = "application/json";
    const token = await opts.getToken?.();
    if (token) headers.authorization = `Bearer ${token}`;
    else if (route.auth) throw new ApiRequestError(401, { error: "Sign in required" }, "Sign in required");
    const qs = new URLSearchParams(Object.entries(args.query ?? {}).filter((e): e is [string, string] => !!e[1])).toString();
    const res = await f(opts.baseUrl.replace(/\/$/, "") + fill(route.path, args.params ?? {}) + (qs ? `?${qs}` : ""), {
      method: route.method,
      headers,
      body: args.body === undefined ? undefined : JSON.stringify(args.body),
    });
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : undefined;
    if (!res.ok) {
      const parsed = ApiError.safeParse(json);
      throw new ApiRequestError(res.status, parsed.success ? parsed.data : undefined, parsed.success ? parsed.data.error : `HTTP ${res.status}`);
    }
    return route.res.parse(json);
  }

  return {
    me: () => call(routes.me),
    catalog: () => call(routes.catalog),
    designVersions: (id: string) => call(routes.designVersions, { params: { id } }),
    designCommunity: (id: string) => call(routes.designCommunity, { params: { id } }),
    recordDownload: (id: string, body: RecordDownloadBody = {}) => call(routes.recordDownload, { params: { id }, body }),
    downloadStats: () => call(routes.downloadStats),
    listConfigs: () => call(routes.listConfigs),
    createConfig: (body: CreateConfigBody) => call(routes.createConfig, { body }),
    getConfig: (id: string) => call(routes.getConfig, { params: { id } }),
    updateConfig: (id: string, body: UpdateConfigBody) => call(routes.updateConfig, { params: { id }, body }),
    deleteConfig: (id: string) => call(routes.deleteConfig, { params: { id } }),
    shareConfig: (id: string) => call(routes.shareConfig, { params: { id } }),
    getShared: (token: string) => call(routes.getShared, { params: { token } }),
    setProfile: (body: SetProfileBody) => call(routes.setProfile, { body }),
    tagDesign: (id: string, body: TagBody) => call(routes.tagDesign, { params: { id }, body }),
    listFits: () => call(routes.listFits),
    lookupFits: (q: string) => call(routes.lookupFits, { query: { q } }),
    listPublic: (q: { fit?: string; design?: string; cursor?: string; limit?: number } = {}) =>
      call(routes.listPublic, { query: { fit: q.fit, design: q.design, cursor: q.cursor, limit: q.limit?.toString() } }),
    getPublic: (id: string) => call(routes.getPublic, { params: { id } }),
    confirmPublic: (id: string, ref: string) => call(routes.confirmPublic, { params: { id }, body: { ref } }),
    removeTag: (id: string, ref: string) => call(routes.removeTag, { params: { id, ref } }),
    likePublic: (id: string) => call(routes.likePublic, { params: { id } }),
    unlikePublic: (id: string) => call(routes.unlikePublic, { params: { id } }),
    myLikes: () => call(routes.myLikes),
    myCommunity: () => call(routes.myCommunity),
    reportPublic: (id: string, body: ReportBody) => call(routes.reportPublic, { params: { id }, body }),
    modQueue: () => call(routes.modQueue),
    modAction: (id: string, ref: string, body: ModActionBody) => call(routes.modAction, { params: { id, ref }, body }),
    track: (events: AnalyticsEvent[]) => call(routes.track, { body: { events } }),
  };
}
export type ApiClient = ReturnType<typeof createApiClient>;
